import React, { useState, useEffect, useContext } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { AuthContext } from '../../../contexts/AuthContext';
import { periodLockService } from '../../../services/periodLockService';
import { currencyAdjustmentsService } from '../../../services/currencyAdjustmentsService';

const PeriodEndControls = () => {
  const { user, userProfile } = useContext(AuthContext);
  const [periodLocks, setPeriodLocks] = useState([]);
  const [currencyAdjustments, setCurrencyAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [showCurrencyForm, setShowCurrencyForm] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [selectedLock, setSelectedLock] = useState(null);

  const [periodFormData, setPeriodFormData] = useState({
    period_name: '',
    period_start_date: '',
    period_end_date: '',
    lock_type: 'month_end',
    affected_modules: ['general_ledger', 'accounts_payable', 'accounts_receivable']
  });

  const [currencyFormData, setCurrencyFormData] = useState({
    adjustment_type: 'revaluation',
    reference_number: '',
    description: '',
    adjustment_date: new Date()?.toISOString()?.split('T')?.[0],
    base_currency: 'USD',
    foreign_currency: 'EUR',
    original_rate: '',
    new_rate: '',
    amount_in_base_currency: '',
    amount_in_foreign_currency: ''
  });

  const [revaluationResult, setRevaluationResult] = useState(null);

  const lockTypes = [
    { value: 'month_end', label: 'Month End' },
    { value: 'quarter_end', label: 'Quarter End' },
    { value: 'year_end', label: 'Year End' },
    { value: 'audit', label: 'Audit Period' }
  ];

  const adjustmentTypes = [
    { value: 'revaluation', label: 'Revaluation' },
    { value: 'translation', label: 'Translation' },
    { value: 'hedge_adjustment', label: 'Hedge Adjustment' },
    { value: 'spot_rate_change', label: 'Spot Rate Change' }
  ];

  const supportedCurrencies = currencyAdjustmentsService?.getSupportedCurrencies();

  useEffect(() => {
    if (userProfile?.organizations?.[0]?.id) {
      loadData();
    }
  }, [userProfile]);

  const loadData = async () => {
    setLoading(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      // Load period locks
      const locksResult = await periodLockService?.getPeriodLocks(organizationId, {
        page: 1,
        limit: 50
      });
      
      if (locksResult?.data) {
        setPeriodLocks(locksResult?.data);
      }

      // Load currency adjustments
      const currencyResult = await currencyAdjustmentsService?.getCurrencyAdjustments(organizationId, {
        page: 1,
        limit: 20
      });

      if (currencyResult?.data) {
        setCurrencyAdjustments(currencyResult?.data);
      }
    } catch (error) {
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePeriodLock = async (e) => {
    e?.preventDefault();
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    setCreating(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      const result = await periodLockService?.createPeriodLock({
        organization_id: organizationId,
        created_by: user?.id,
        ...periodFormData,
        affected_modules: JSON.stringify(periodFormData?.affected_modules)
      });

      if (result?.error) {
        alert('Error creating period lock: ' + result?.error);
        return;
      }

      setPeriodFormData({
        period_name: '',
        period_start_date: '',
        period_end_date: '',
        lock_type: 'month_end',
        affected_modules: ['general_ledger', 'accounts_payable', 'accounts_receivable']
      });
      setShowPeriodForm(false);
      await loadData();
      alert('Period lock created successfully!');
    } catch (error) {
      console.error('Create period lock error:', error);
      alert('Failed to create period lock');
    } finally {
      setCreating(false);
    }
  };

  const handleCalculateRevaluation = async () => {
    try {
      const result = await currencyAdjustmentsService?.calculateCurrencyRevaluation(
        {
          baseAmount: parseFloat(currencyFormData?.amount_in_base_currency),
          foreignAmount: parseFloat(currencyFormData?.amount_in_foreign_currency)
        },
        parseFloat(currencyFormData?.original_rate),
        parseFloat(currencyFormData?.new_rate)
      );

      if (result?.error) {
        alert('Calculation error: ' + result?.error);
        return;
      }

      setRevaluationResult(result);
    } catch (error) {
      console.error('Calculate revaluation error:', error);
      alert('Failed to calculate revaluation');
    }
  };

  const handleCreateCurrencyAdjustment = async (e) => {
    e?.preventDefault();
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    setCreating(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      const result = await currencyAdjustmentsService?.createCurrencyAdjustment({
        organization_id: organizationId,
        created_by: user?.id,
        ...currencyFormData,
        original_rate: parseFloat(currencyFormData?.original_rate),
        new_rate: parseFloat(currencyFormData?.new_rate),
        amount_in_base_currency: parseFloat(currencyFormData?.amount_in_base_currency),
        amount_in_foreign_currency: parseFloat(currencyFormData?.amount_in_foreign_currency),
        adjustment_amount: revaluationResult?.adjustmentAmount || 0
      });

      if (result?.error) {
        alert('Error creating currency adjustment: ' + result?.error);
        return;
      }

      setCurrencyFormData({
        adjustment_type: 'revaluation',
        reference_number: '',
        description: '',
        adjustment_date: new Date()?.toISOString()?.split('T')?.[0],
        base_currency: 'USD',
        foreign_currency: 'EUR',
        original_rate: '',
        new_rate: '',
        amount_in_base_currency: '',
        amount_in_foreign_currency: ''
      });
      setRevaluationResult(null);
      setShowCurrencyForm(false);
      await loadData();
      alert('Currency adjustment created successfully!');
    } catch (error) {
      console.error('Create currency adjustment error:', error);
      alert('Failed to create currency adjustment');
    } finally {
      setCreating(false);
    }
  };

  const handleLockPeriod = async (lockId, lockType) => {
    if (!user?.id) return;

    try {
      const result = await periodLockService?.lockPeriod(lockId, user?.id, lockType);
      if (result?.error) {
        alert('Error locking period: ' + result?.error);
        return;
      }

      await loadData();
      alert(`Period ${lockType?.replace('_', ' ')} successfully!`);
    } catch (error) {
      console.error('Lock period error:', error);
      alert('Failed to lock period');
    }
  };

  const handleUnlockPeriod = async () => {
    if (!user?.id || !selectedLock || !unlockReason?.trim()) return;

    try {
      const result = await periodLockService?.unlockPeriod(selectedLock?.id, user?.id, unlockReason);
      if (result?.error) {
        alert('Error unlocking period: ' + result?.error);
        return;
      }

      setSelectedLock(null);
      setUnlockReason('');
      await loadData();
      alert('Period unlocked successfully!');
    } catch (error) {
      console.error('Unlock period error:', error);
      alert('Failed to unlock period');
    }
  };

  const handleCreateMonthlyLocks = async () => {
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    const year = new Date()?.getFullYear();
    const confirm = window.confirm(`Create monthly period locks for ${year}?`);
    
    if (!confirm) return;

    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      const result = await periodLockService?.createMonthlyPeriodLocks(organizationId, user?.id, year);
      
      if (result?.error) {
        alert('Error creating monthly locks: ' + result?.error);
        return;
      }

      await loadData();
      alert(`Monthly period locks for ${year} created successfully!`);
    } catch (error) {
      console.error('Create monthly locks error:', error);
      alert('Failed to create monthly locks');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-green-100 text-green-800',
      soft_lock: 'bg-yellow-100 text-yellow-800',
      hard_lock: 'bg-orange-100 text-orange-800',
      closed: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors?.[status] || colors?.open}`}>
        {status?.replace('_', ' ')?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Period-End Controls</h2>
          <p className="text-muted-foreground mt-1">Transaction locking, currency adjustments, and closing procedures</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <Button 
            onClick={() => setShowCurrencyForm(!showCurrencyForm)}
            variant="outline"
            iconName="DollarSign"
            iconPosition="left"
          >
            Currency Adjustment
          </Button>
          <Button 
            onClick={() => setShowPeriodForm(!showPeriodForm)}
            iconName="Lock"
            iconPosition="left"
          >
            New Period Lock
          </Button>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button 
            onClick={handleCreateMonthlyLocks}
            variant="outline"
            iconName="Calendar"
            size="sm"
          >
            Create Monthly Locks
          </Button>
          <Button 
            variant="outline"
            iconName="FileSpreadsheet"
            size="sm"
            onClick={() => alert('Generating period-end report...')}
          >
            Generate Report
          </Button>
          <Button 
            variant="outline"
            iconName="CheckSquare"
            size="sm"
            onClick={() => alert('Running period-end validation...')}
          >
            Run Validation
          </Button>
        </div>
      </div>
      {/* Currency Adjustment Form */}
      {showCurrencyForm && (
        <form onSubmit={handleCreateCurrencyAdjustment} className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Currency Adjustment</h3>
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Select
              label="Adjustment Type"
              value={currencyFormData?.adjustment_type}
              onChange={(value) => setCurrencyFormData({...currencyFormData, adjustment_type: value})}
              options={adjustmentTypes}
              required
            />
            <Input
              label="Reference Number"
              value={currencyFormData?.reference_number}
              onChange={(e) => setCurrencyFormData({...currencyFormData, reference_number: e?.target?.value})}
              placeholder="Enter reference number"
              required
            />
            <Input
              label="Adjustment Date"
              type="date"
              value={currencyFormData?.adjustment_date}
              onChange={(e) => setCurrencyFormData({...currencyFormData, adjustment_date: e?.target?.value})}
              required
            />
          </div>

          <div className="mb-6">
            <Input
              label="Description"
              value={currencyFormData?.description}
              onChange={(e) => setCurrencyFormData({...currencyFormData, description: e?.target?.value})}
              placeholder="Enter adjustment description"
              required
            />
          </div>

          {/* Currency Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Select
              label="Base Currency"
              value={currencyFormData?.base_currency}
              onChange={(value) => setCurrencyFormData({...currencyFormData, base_currency: value})}
              options={supportedCurrencies?.map(curr => ({ value: curr?.code, label: `${curr?.code} - ${curr?.name}` }))}
            />
            <Select
              label="Foreign Currency"
              value={currencyFormData?.foreign_currency}
              onChange={(value) => setCurrencyFormData({...currencyFormData, foreign_currency: value})}
              options={supportedCurrencies?.map(curr => ({ value: curr?.code, label: `${curr?.code} - ${curr?.name}` }))}
            />
          </div>

          {/* Rates and Amounts */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Input
              label="Original Rate"
              type="number"
              step="0.000001"
              value={currencyFormData?.original_rate}
              onChange={(e) => setCurrencyFormData({...currencyFormData, original_rate: e?.target?.value})}
              placeholder="0.000000"
              required
            />
            <Input
              label="New Rate"
              type="number"
              step="0.000001"
              value={currencyFormData?.new_rate}
              onChange={(e) => setCurrencyFormData({...currencyFormData, new_rate: e?.target?.value})}
              placeholder="0.000000"
              required
            />
            <Input
              label="Base Currency Amount"
              type="number"
              step="0.01"
              value={currencyFormData?.amount_in_base_currency}
              onChange={(e) => setCurrencyFormData({...currencyFormData, amount_in_base_currency: e?.target?.value})}
              placeholder="0.00"
              required
            />
            <Input
              label="Foreign Currency Amount"
              type="number"
              step="0.01"
              value={currencyFormData?.amount_in_foreign_currency}
              onChange={(e) => setCurrencyFormData({...currencyFormData, amount_in_foreign_currency: e?.target?.value})}
              placeholder="0.00"
              required
            />
          </div>

          <div className="flex justify-between items-center mb-6">
            <Button
              type="button"
              onClick={handleCalculateRevaluation}
              variant="outline"
              iconName="Calculator"
            >
              Calculate Revaluation
            </Button>

            {revaluationResult && (
              <div className="text-sm">
                <span className="text-muted-foreground">Adjustment: </span>
                <span className={`font-medium ${revaluationResult?.adjustmentAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${revaluationResult?.adjustmentAmount?.toFixed(2)} ({revaluationResult?.gainLoss})
                </span>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => setShowCurrencyForm(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || !revaluationResult}
              iconName={creating ? "Loader2" : "Save"}
              className={creating ? "animate-spin" : ""}
            >
              {creating ? 'Creating...' : 'Create Adjustment'}
            </Button>
          </div>
        </form>
      )}
      {/* Period Lock Form */}
      {showPeriodForm && (
        <form onSubmit={handleCreatePeriodLock} className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">New Period Lock</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Input
              label="Period Name"
              value={periodFormData?.period_name}
              onChange={(e) => setPeriodFormData({...periodFormData, period_name: e?.target?.value})}
              placeholder="Enter period name"
              required
            />
            <Select
              label="Lock Type"
              value={periodFormData?.lock_type}
              onChange={(value) => setPeriodFormData({...periodFormData, lock_type: value})}
              options={lockTypes}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Input
              label="Period Start Date"
              type="date"
              value={periodFormData?.period_start_date}
              onChange={(e) => setPeriodFormData({...periodFormData, period_start_date: e?.target?.value})}
              required
            />
            <Input
              label="Period End Date"
              type="date"
              value={periodFormData?.period_end_date}
              onChange={(e) => setPeriodFormData({...periodFormData, period_end_date: e?.target?.value})}
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => setShowPeriodForm(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating}
              iconName={creating ? "Loader2" : "Lock"}
              className={creating ? "animate-spin" : ""}
            >
              {creating ? 'Creating...' : 'Create Lock'}
            </Button>
          </div>
        </form>
      )}
      {/* Period Locks List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Period Locks</h3>
        </div>
        
        {periodLocks?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Period Locks</h3>
            <p className="text-muted-foreground">Create your first period lock to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {periodLocks?.map((lock) => (
              <div key={lock?.id} className="px-6 py-4 hover:bg-muted/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-foreground">{lock?.period_name}</span>
                      {getStatusBadge(lock?.lock_status)}
                      <span className="text-sm text-muted-foreground">
                        {lock?.lock_type?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {new Date(lock?.period_start_date)?.toLocaleDateString()} - {new Date(lock?.period_end_date)?.toLocaleDateString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Created by {lock?.created_by?.full_name} • 
                      {lock?.locked_by && ` Locked by ${lock?.locked_by?.full_name}`}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {lock?.lock_status === 'open' && (
                      <>
                        <Button
                          onClick={() => handleLockPeriod(lock?.id, 'soft_lock')}
                          size="sm"
                          iconName="LockKeyhole"
                          variant="outline"
                        >
                          Soft Lock
                        </Button>
                        <Button
                          onClick={() => handleLockPeriod(lock?.id, 'hard_lock')}
                          size="sm"
                          iconName="Lock"
                          variant="outline"
                        >
                          Hard Lock
                        </Button>
                      </>
                    )}
                    
                    {lock?.lock_status === 'soft_lock' && (
                      <>
                        <Button
                          onClick={() => handleLockPeriod(lock?.id, 'hard_lock')}
                          size="sm"
                          iconName="Lock"
                          variant="outline"
                        >
                          Hard Lock
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedLock(lock);
                            setUnlockReason('');
                          }}
                          size="sm"
                          iconName="Unlock"
                          variant="ghost"
                        >
                          Unlock
                        </Button>
                      </>
                    )}

                    {lock?.lock_status === 'hard_lock' && (
                      <>
                        <Button
                          onClick={() => handleLockPeriod(lock?.id, 'closed')}
                          size="sm"
                          iconName="XCircle"
                          variant="outline"
                        >
                          Close
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedLock(lock);
                            setUnlockReason('');
                          }}
                          size="sm"
                          iconName="Unlock"
                          variant="ghost"
                        >
                          Unlock
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Currency Adjustments List */}
      {currencyAdjustments?.length > 0 && (
        <div className="bg-card border border-border rounded-lg">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Recent Currency Adjustments</h3>
          </div>
          
          <div className="divide-y divide-border">
            {currencyAdjustments?.map((adjustment) => (
              <div key={adjustment?.id} className="px-6 py-4 hover:bg-muted/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-foreground">{adjustment?.reference_number}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {adjustment?.foreign_currency} → {adjustment?.base_currency}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(adjustment?.adjustment_date)?.toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-foreground mb-1">{adjustment?.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>Rate: {adjustment?.original_rate} → {adjustment?.new_rate}</span>
                      <span>•</span>
                      <span className={adjustment?.adjustment_amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                        Adjustment: ${adjustment?.adjustment_amount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Unlock Modal */}
      {selectedLock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Unlock Period: {selectedLock?.period_name}</h3>
            <p className="text-muted-foreground mb-4">
              Please provide a reason for unlocking this period. This action will be logged.
            </p>
            
            <textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e?.target?.value)}
              placeholder="Reason for unlocking period..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
              required
            />

            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setSelectedLock(null);
                  setUnlockReason('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnlockPeriod}
                disabled={!unlockReason?.trim()}
                iconName="Unlock"
              >
                Unlock Period
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodEndControls;